import { makeStyles } from "tss-react/mui";
import { Box, Fade, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      height: "88px",
      width: "88px",
      zIndex: 10
    },
    buttonContainer: {
      position: "relative",
      height: "88px",
      width: "88px",
      borderRadius: theme.spacing(1),
      background: theme.palette.background.default,
      boxShadow: "3px 10px 3px 0px rgba(0, 0, 0, 0.00), 2px 6px 3px 0px rgba(0, 0, 0, 0.01), 1px 4px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 0px 1px 0px rgba(0, 0, 0, 0.10)",
      padding: theme.spacing(0.5),
      cursor: "pointer",
      "&:hover": {
        "&:after": {
          zIndex: 100,
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          background: theme.palette.action.hover,
          borderRadius: theme.spacing(1),
          content: "''",
          display: "block",
          pointerEvents: "none"
        },
      },
      "&:before": {
        zIndex: 1,
        position: "absolute",
        top: "4px",
        left: "4px",
        padding: "8px",
        height: "calc(100% - 8px)",
        width: "calc(100% - 8px)",
        borderRadius: "4px",
        content: "''",
        display: "block",
        pointerEvents: "none",
        background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 39.87%, rgba(0, 0, 0, 0.48) 100%)"

      }
    },
    buttonImage: {
      position: "relative",
      width: "100%",
      height: "100%",
      borderRadius: "4px",
      background: theme.palette.divider,
      objectFit: "cover",
    },
    selectedStyleText: {
      position: "absolute",
      zIndex: 10,
      bottom: theme.spacing(1),
      left: 0,
      width: "100%",
      color: "#FFF"
    },
    iconButton: {
      width: "100%",
      height: "100%",
    },
    contentContainer: {
      zIndex: 99,
      position: "absolute",
      bottom: 0,
      left: `calc(100% + ${theme.spacing(1.5)})`,
      minWidth: "100px",
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
      background: theme.palette.background.default,
      boxShadow: "3px 10px 3px 0px rgba(0, 0, 0, 0.00), 2px 6px 3px 0px rgba(0, 0, 0, 0.01), 1px 4px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 0px 1px 0px rgba(0, 0, 0, 0.10)",
    },
    contentContainerSlim: {
      position: "absolute",
      bottom: `calc(100% + ${theme.spacing(1.5)})`,
      maxWidth: `calc(100vw - ${theme.spacing(3)})`,
      overflowY: "auto",
      overflowX: "hidden",
      left: 0,
      minWidth: "100px",
      padding: theme.spacing(1),
      borderRadius: theme.spacing(1),
      background: theme.palette.background.default,
      boxShadow: "3px 10px 3px 0px rgba(0, 0, 0, 0.00), 2px 6px 3px 0px rgba(0, 0, 0, 0.01), 1px 4px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 0px 1px 0px rgba(0, 0, 0, 0.10)",
    },
    option: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      userSelect: "none",
      "& img": {
        width: "56px",
        height: "56px",
        borderRadius: theme.spacing(1),
        background: theme.palette.divider,
        marginBottom: theme.spacing(0.5),
        objectFit: "cover"
      },
      "&:hover": {
        "&:after": {
          zIndex: 100,
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          background: theme.palette.action.hover,
          borderRadius: theme.spacing(1),
          content: "''",
          display: "block",
          pointerEvents: "none"
        },
      }
    }
  })
);

export default function StyleSelector(props) {
  const { className, expanded, setExpanded, onChange, value, slim } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const onToggle = useCallback(() => {
    setExpanded(t => !t);
  }, []);

  const styles = useMemo(() => [
    {
      id: "standard",
      name: t("operationsMap:styles:standard"),
      url: "mapbox://styles/mapbox/standard",
      image: "/assets/images/map/styles/standard.png",
    },
    {
      id: "satellite",
      name: t("operationsMap:styles:satellite"),
      url: "mapbox://styles/mapbox/satellite-streets-v12",
      image: "/assets/images/map/styles/satellite.png",
    },
    {
      id: "dark",
      name: t("operationsMap:styles:dark"),
      url: "mapbox://styles/mapbox/dark-v11",
      image: "/assets/images/map/styles/dark.png",
    },
    {
      id: "terrain",
      name: t("operationsMap:styles:terrain"),
      url: "mapbox://styles/mapbox/outdoors-v12",
      image: "/assets/images/map/styles/terrain.png",
    },
    {
      id: "light",
      name: t("operationsMap:styles:light"),
      url: "mapbox://styles/mapbox/light-v11",
      image: "/assets/images/map/styles/mono.png",
    },
  ], [t]);


  const selectedStyle = useMemo(() => styles.find(t => t.id === value) || styles[0], [styles, value]);


  // TODO after expand!!

  return (
    <Box className={`${classes.root} ${className || ""}`}>

      <Box className={classes.buttonContainer} onClick={onToggle}>
        <img src={selectedStyle.image} className={classes.buttonImage}></img>
        <Typography variant="caption" textAlign="center" className={classes.selectedStyleText}>{selectedStyle.name}</Typography>
      </Box>

      <Fade in={expanded}>
        <Box className={slim ? classes.contentContainerSlim : classes.contentContainer}>
          <Stack direction="row" spacing={1}>
            {
              styles.filter(style => style.id !== value).map((style, key) => (
                <Box className={classes.option} key={key} onClick={() => onChange(style)}>
                  <img src={style.image}></img>
                  <Typography variant="caption" color="textSecondary">{style.name}</Typography>
                </Box>
              ))
            }
          </Stack>
        </Box>
      </Fade>

    </Box>
  );
}

